import type { ReactNode } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useThemeState } from '@/context/ThemeContext';
import type { ThemeMode } from '@/lib/types';
import { MonitorIcon, MoonIcon, SunIcon } from './Icons';

const THEME_OPTIONS: Array<{
  mode: ThemeMode;
  label: string;
  description: string;
  icon: ReactNode;
}> = [
  {
    mode: 'system',
    label: 'System',
    description: 'Follow the device appearance.',
    icon: <MonitorIcon size={18} />,
  },
  {
    mode: 'light',
    label: 'Light',
    description: 'Use the bright palette all the time.',
    icon: <SunIcon size={18} />,
  },
  {
    mode: 'dark',
    label: 'Dark',
    description: 'Use the dark palette all the time.',
    icon: <MoonIcon size={18} />,
  },
];

function activeThemeIcon(mode: ThemeMode) {
  switch (mode) {
    case 'light':
      return <SunIcon size={18} />;
    case 'dark':
      return <MoonIcon size={18} />;
    case 'system':
    default:
      return <MonitorIcon size={18} />;
  }
}

function activeThemeLabel(mode: ThemeMode) {
  switch (mode) {
    case 'light':
      return 'Light';
    case 'dark':
      return 'Dark';
    case 'system':
    default:
      return 'System';
  }
}

export function ThemeMenuButton() {
  const { themeMode, setThemeMode } = useThemeState();

  return (
    <Menu as="div" className="relative">
      <MenuButton
        className="icon-button inline-flex items-center justify-center"
        aria-label={`Theme mode, currently ${activeThemeLabel(themeMode)}`}
        title={`Theme mode: ${activeThemeLabel(themeMode)}`}>
        {activeThemeIcon(themeMode)}
      </MenuButton>

      <MenuItems anchor="bottom end" className="menu-panel" transition>
        <div className="menu-panel-header">
          <div className="menu-panel-kicker">Theme mode</div>
          <div className="menu-panel-title">{activeThemeLabel(themeMode)} is active</div>
        </div>

        <div className="menu-list">
          {THEME_OPTIONS.map((option) => {
            const selected = option.mode === themeMode;

            return (
              <MenuItem key={option.mode}>
                {({ focus }) => (
                  <button
                    type="button"
                    className={`menu-option${focus ? ' active' : ''}`}
                    onClick={() => {
                      void setThemeMode(option.mode);
                    }}>
                    <span className="menu-option-icon">{option.icon}</span>
                    <span className="menu-option-content">
                      <strong className="menu-option-title">{option.label}</strong>
                      <span className="menu-option-text">{option.description}</span>
                    </span>
                    {selected ? <span className="badge soft shrink-0">Active</span> : null}
                  </button>
                )}
              </MenuItem>
            );
          })}
        </div>
      </MenuItems>
    </Menu>
  );
}
